import { NextPage } from "next"
import Head from "next/head"
import { useRouter } from "next/router"
import { useCallback, useEffect, useRef } from "react"
import { DefaultLayout } from "src/components/Layout"
import { DunningEvaluationSandbox } from "src/features/dunning/components/DunningEvaluationSandbox"
import { SystemPromptSettingsView } from "src/features/dunning/components/SystemPromptSettingsView"
import { RegressionTest } from "src/features/dunning/evaluation/batch/RegressionTest"
import { useIsRouterReady } from "src/hooks/useIsRouterReady"
import {
  LEGACY_BATCH_TAB,
  SETTINGS_PATH,
  SETTINGS_TAB_LABELS,
  parseSettingsQuery,
  settingsQuery,
} from "src/features/dunning/evaluation/navigation"
import type {
  Locate,
  RegisterLeaveGuard,
  SettingsLocation,
  SettingsTab,
} from "src/features/dunning/evaluation/navigation"

const TABS: { value: SettingsTab; label: string }[] = (
  ["versions", "regression", "sandbox", "flows"] as const
).map((value) => ({ value, label: SETTINGS_TAB_LABELS[value] }))

// ケースの枠（data-detail-fill）を含む間だけ、xl でページを画面の高さに収め、残りの高さを枠まで渡すクラス
const FIT_PAGE =
  "xl:has-[[data-detail-fill]]:flex xl:has-[[data-detail-fill]]:h-[calc(100vh_-_var(--header-stack-height,60px)_-_32px)] xl:has-[[data-detail-fill]]:min-h-0 xl:has-[[data-detail-fill]]:flex-col"
const FIT_CHILD =
  "xl:has-[[data-detail-fill]]:flex xl:has-[[data-detail-fill]]:min-h-0 xl:has-[[data-detail-fill]]:flex-1 xl:has-[[data-detail-fill]]:flex-col"
// ケースを開いている間は、中身（左右のペイン）を広く見せるため、ページの見出しを隠し、幅の上限と上下の余白を詰める
const CASE_OPEN_PAGE =
  "has-[[data-detail-fill]]:max-w-none has-[[data-detail-fill]]:px-4 has-[[data-detail-fill]]:pt-2"
const CASE_OPEN_STACK = "has-[[data-detail-fill]]:gap-2"
const CASE_OPEN_HIDDEN = "group-has-[[data-detail-fill]]/settings:hidden"
const CASE_OPEN_TAB = "group-has-[[data-detail-fill]]/settings:py-2"
const CASE_OPEN_CARD = "has-[[data-detail-fill]]:px-3 has-[[data-detail-fill]]:pt-2"

const DunningPromptSettingsPage: NextPage = () => {
  const router = useRouter()
  const isRouterReady = useIsRouterReady()
  // タブ・プロンプトの比較の実行・ケースは URL に持つ（タブをまたいで開く・再読み込みで戻るため）
  const location = parseSettingsQuery(router.query)
  const activeTab = location.tab
  const locate: Locate = useCallback(
    (next, options) => {
      const url = { pathname: SETTINGS_PATH, query: settingsQuery(next) }
      if (options?.replace) void router.replace(url, undefined, { shallow: true })
      else void router.push(url, undefined, { shallow: true })
    },
    [router]
  )
  // 開いている画面が未保存の変更を抱えていれば、タブの切り替えもその画面の確認を通す（確認なしで編集が消えないように）
  const leaveGuard = useRef<((to: SettingsLocation) => void) | null>(null)
  const registerLeaveGuard: RegisterLeaveGuard = useCallback((guard) => {
    leaveGuard.current = guard
  }, [])
  const switchTab = (to: SettingsLocation) => (leaveGuard.current ?? locate)(to)
  const caseTab = activeTab === "sandbox" || activeTab === "flows"
  // 「プロンプトの比較」に統合した旧「まとめて評価」タブの URL（tab=batch）は、実行・ケースを引き継いでプロンプトの比較の URL に置き換える
  const legacyBatchTab = router.query.tab === LEGACY_BATCH_TAB
  useEffect(() => {
    if (legacyBatchTab) locate(location, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyBatchTab])

  return (
    <DefaultLayout>
      <Head>
        <title>AIプロンプト設定 | for Smoother</title>
      </Head>
      {/* 1件ずつ試す・お客様の流れでケースを開いている間（枠 data-detail-fill を含む間）は、xl でページを画面の高さに収め
          （ページ自体はスクロールさせない）、見出し・タブの下の残りの高さをケースの枠（左右のペインがそれぞれスクロールする）に渡す */}
      <div className={`group/settings min-h-[calc(100vh_-_90px)] bg-gray-50 ${FIT_PAGE}`}>
        {/* ケースの枠は画面の残りの高さいっぱいに出すので、1件ずつ試す・お客様の流れでは下の余白を付けない */}
        <div
          className={`mx-auto w-full max-w-[1400px] px-6 pt-6 ${caseTab ? "" : "pb-6"} ${CASE_OPEN_PAGE} ${FIT_CHILD}`}
        >
          <div className={`flex min-w-0 flex-col gap-5 ${CASE_OPEN_STACK} ${FIT_CHILD}`}>
            <div className={`flex items-baseline gap-4 ${CASE_OPEN_HIDDEN}`}>
              <h1 className="text-[1.9rem] font-bold tracking-tight text-gray-900">
                AIプロンプト設定
              </h1>
            </div>
            {/* タブ: 督促ケース一覧と同じ下線パターン。
                design system にタブ部品が無いため、色だけ semantic トークンで当てる */}
            <div role="tablist" className="flex flex-wrap gap-1 border-b border-border-default">
              {TABS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === t.value}
                  // プロンプトの比較と1件ずつ試すの間では、見ている実行を引き継ぐ（お客様の流れは実行と突き合わせない）
                  onClick={() =>
                    switchTab({
                      tab: t.value,
                      batch: t.value === "versions" || t.value === "flows" ? null : location.batch,
                    })
                  }
                  className={`-mb-px border-b-2 px-5 py-3 text-[1.4rem] text-text-default transition-colors ${CASE_OPEN_TAB} ${
                    activeTab === t.value
                      ? "border-action-primary font-bold"
                      : "border-transparent font-medium hover:border-border-default"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {activeTab === "regression" ? (
              <div className="rounded-xl border border-border-default bg-bg-default px-4 py-5 min-[768px]:px-6">
                <RegressionTest batchId={location.batch} caseId={location.case} onLocate={locate} />
              </div>
            ) : activeTab === "flows" ? (
              <div
                className={`rounded-xl border border-border-default bg-bg-default px-6 pt-5 ${CASE_OPEN_CARD} ${FIT_CHILD}`}
              >
                <DunningEvaluationSandbox
                  key="flows"
                  variant="flows"
                  caseId={location.case}
                  source={location.source}
                  onLocate={locate}
                  onRegisterLeaveGuard={registerLeaveGuard}
                />
              </div>
            ) : activeTab === "sandbox" ? (
              <div
                className={`rounded-xl border border-border-default bg-bg-default px-6 pt-5 ${CASE_OPEN_CARD} ${FIT_CHILD}`}
              >
                <DunningEvaluationSandbox
                  key="sandbox"
                  batchId={location.batch}
                  caseId={location.case}
                  from={location.from}
                  onLocate={locate}
                  onRegisterLeaveGuard={registerLeaveGuard}
                />
              </div>
            ) : isRouterReady ? (
              // URL のクエリを読むまでは出さない（タブ指定で開いたときに別のタブを一瞬読み込まない）
              <SystemPromptSettingsView onLocate={locate} />
            ) : null}
          </div>
        </div>
      </div>
    </DefaultLayout>
  )
}

export default DunningPromptSettingsPage
